import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslateService } from '@ngx-translate/core';
import { ApiErrorMessageFormatterService } from './api-error-message-formatter.service';

export interface ApiError {
    httpCode: number;
    code: string;
    message: string;
    details?: { retryAfter?: string | null } & Record<string, unknown>;
}

export interface ApiErrorTexts {
    titleKey: string;
    messageKey: string;
}

@Injectable({
    providedIn: 'root',
})
export class ApiErrorHandlerService {
    constructor(
        private readonly messageService: MessageService,
        private readonly translate: TranslateService,
        private readonly messageFormatter: ApiErrorMessageFormatterService,
    ) {}

    /**
     * Handles an ApiError by displaying a toast message to the user.
     * The toast message is only displayed if the translation exists.
     * @param error The ApiError to be handled.
     * @returns true if the error was handled, false otherwise.
     */
    public handleError(error: ApiError): boolean {
        const title = this.getToastTitle(error);
        // console.log(title);

        if (this.isTranslated(error, title)) {
            this.messageService.add({
                severity: 'error',
                summary: title,
                detail: this.getToastMessage(error),
                life: 7000,
            });
            return true;
        }
        return false;
    }

    /**
     * Checks whether this error has a translated message, so a caller can skip its own fallback
     * message when the global handler already shows one for it.
     * @param error The ApiError to check.
     * @returns true if a translated message exists.
     */
    public canHandle(error: ApiError): boolean {
        return this.isTranslated(error, this.getToastTitle(error));
    }

    private getToastTitle(error: ApiError): string {
        return this.translate.instant(this.getTitleKey(error));
    }

    private getTitleKey(error: ApiError): string {
        return 'apiErrors.' + error.code + '_title';
    }

    private getToastMessage(error: ApiError): string {
        return this.messageFormatter.getMessage(error);
    }

    private isTranslated(error: ApiError, title: string): boolean {
        return title !== '' && title !== this.getTitleKey(error);
    }
}
